/**
 * Integrytas Group - Base App (MOBILE)
 * Page: PFW.PORTAL_ADMIN — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/PfwPortalAdminPage.tsx. A DISTINCT
 * presentation (company picker + segmented card lists + bottom-sheet detail) over
 * the SAME data/logic layer only:
 *   - reads via apiDb (pfw_company / pfw_company_role + _tl / pfw_company_role_page
 *     roll-up / pfw_portal_user / pfw_portal_access_log), resolving role names
 *     against the ACTIVE locale (§8, §17.B4 — never a raw id or code reaches
 *     render), Y/N as status pills and the portal-user status CHECK-enum through a
 *     labelled i18n constant (§17.B11).
 *
 * READ-ONLY on mobile, by design. The desktop screen's every mutation goes through
 * delegated SECURITY DEFINER RPCs (pfw_upsert_company_role, pfw_set_company_role_pages,
 * pfw_admin_create_portal_user, pfw_set_company_ceiling, …) that re-check company-admin
 * authority server-side — NOT the Common-Routine table-write path (routedUpdate /
 * routedInsert, §2) that mobile writes must use. Rather than invent a parallel write
 * path, the delegated administration (create/edit roles + users, page grants, the
 * platform-admin allocation ceiling) stays on the desktop; mobile surfaces the same
 * information read-only (mirrors how MobileUserEditSheet keeps role editing on desktop).
 * It never re-renders the desktop grid; §16 — and it imports no routed_writes.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, ShieldCheck, User, ScrollText, Building2 } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDateTime } from '../../lib/format_date';
import { MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill, type MobilePillTone } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CompanyRow {
  pfw_company_id: number;
  internal_code: string;
  name: string;
}

interface RoleRow {
  company_role_id: number;
  internal_code: string;
  is_admin_role: 'Y' | 'N';
  is_active: 'Y' | 'N';
  display_text: string;
  pages: number;
}

interface PortalUserRow {
  portal_user_id: string;
  username: string;
  display_name: string | null;
  status: string;
  company_role_id: number | null;
  role_label: string;
}

interface AccessLogRow {
  access_log_id: number;
  view_code: string | null;
  dataset_code: string | null;
  action: string;
  created_at: string;
  username: string;
}

interface TlRow { locale_code: string; display_text: string }

type Segment = 'roles' | 'users' | 'activity';

// Independent is_active facet for the roles list (roles carry is_active); defaults
// to Active-only on first load (§17.B12).
type StatusFacet = 'all' | 'active' | 'inactive';

// §17.B11: portal-user status is a CHECK enum — its label is localized through
// i18n (never the stored literal) and mapped to a status-pill tone.
const USER_STATUS_META: Record<string, { labelKey: string; label: string; tone: MobilePillTone }> = {
  ACTIVE:   { labelKey: 'mobile.pfw.user_status.ACTIVE',   label: 'Active',   tone: 'ok' },
  LOCKED:   { labelKey: 'mobile.pfw.user_status.LOCKED',   label: 'Locked',   tone: 'no' },
  DISABLED: { labelKey: 'mobile.pfw.user_status.DISABLED', label: 'Disabled', tone: 'no' },
  PENDING:  { labelKey: 'mobile.pfw.user_status.PENDING',  label: 'Pending',  tone: 'wait' },
};

const MAX_LOG_ROWS = 50;

function resolveTl(tls: TlRow[] | undefined, activeLocale: string, fallback: string): string {
  const list = tls ?? [];
  const exact = list.find((tl) => tl.locale_code === activeLocale);
  const fb = list.find((tl) => tl.locale_code === 'EN_US') ?? list[0];
  return exact?.display_text ?? fb?.display_text ?? fallback;
}

function initialsOf(name: string): string {
  const base = (name || '?').trim();
  const parts = base.split(/[\s@._-]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : base.slice(0, 2)).toUpperCase();
}

// ---------------------------------------------------------------------------
// Screen — session unused (read-only): props accepted but not destructured.
// ---------------------------------------------------------------------------

export function MobilePfwPortalAdminPage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const formatDateTime = useFormatDateTime();

  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [companyId, setCompanyId] = useState<string>('');
  const [segment, setSegment] = useState<Segment>('roles');
  const [filter, setFilter] = useState('');
  const [activeFacet, setActiveFacet] = useState<StatusFacet>('active');

  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [users, setUsers] = useState<PortalUserRow[]>([]);
  const [log, setLog] = useState<AccessLogRow[]>([]);

  const [selectedRole, setSelectedRole] = useState<RoleRow | null>(null);
  const [selectedUser, setSelectedUser] = useState<PortalUserRow | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Companies (third parties) — loaded once; no _tl, so locale-independent.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error: e } = await apiDb
        .from('pfw_company')
        .select('pfw_company_id, internal_code, name')
        .eq('status', 'ACTIVE')
        .order('name');
      if (cancelled) return;
      if (e) { setError(e.message); return; }
      setCompanies((data ?? []) as CompanyRow[]);
    })();
    return () => { cancelled = true; };
  }, []);

  const reload = async () => {
    if (!companyId) { setRoles([]); setUsers([]); setLog([]); return; }
    setBusy(true);
    setError(null);
    try {
      const cid = Number(companyId);

      // Roles first — their ids drive the page-grant roll-up.
      const { data: rawRoles, error: rErr } = await apiDb
        .from('pfw_company_role')
        .select('company_role_id, internal_code, is_admin_role, is_active, pfw_company_role_tl ( locale_code, display_text )')
        .eq('pfw_company_id', cid)
        .order('internal_code');
      if (rErr) throw rErr;

      type RawRole = {
        company_role_id: number;
        internal_code: string;
        is_admin_role: 'Y' | 'N';
        is_active: 'Y' | 'N';
        pfw_company_role_tl?: TlRow[];
      };
      const rolesFlat = ((rawRoles ?? []) as RawRole[]).map((r) => ({
        company_role_id: r.company_role_id,
        internal_code: r.internal_code,
        is_admin_role: r.is_admin_role,
        is_active: r.is_active,
        display_text: resolveTl(r.pfw_company_role_tl, activeLocale, r.internal_code),
        pages: 0,
      }));
      const roleIds = rolesFlat.map((r) => r.company_role_id);
      const roleLabelById = new Map(rolesFlat.map((r) => [r.company_role_id, r.display_text]));

      const [grantsRes, usersRes, logRes] = await Promise.all([
        roleIds.length
          ? apiDb.from('pfw_company_role_page').select('company_role_id, can_view').in('company_role_id', roleIds)
          : Promise.resolve({ data: [], error: null }),
        apiDb
          .from('pfw_portal_user')
          .select('portal_user_id, username, display_name, status, company_role_id')
          .eq('pfw_company_id', cid)
          .order('username'),
        apiDb
          .from('pfw_portal_access_log')
          .select('access_log_id, view_code, dataset_code, action, created_at, pfw_portal_user!left ( username )')
          .eq('pfw_company_id', cid)
          .order('access_log_id', { ascending: false })
          .limit(MAX_LOG_ROWS),
      ]);

      if (grantsRes.error) throw grantsRes.error;
      if (usersRes.error) throw usersRes.error;
      if (logRes.error) throw logRes.error;

      const viewCounts: Record<number, number> = {};
      for (const g of (grantsRes.data ?? []) as { company_role_id: number; can_view: string }[]) {
        if (g.can_view === 'Y') viewCounts[g.company_role_id] = (viewCounts[g.company_role_id] ?? 0) + 1;
      }
      setRoles(rolesFlat.map((r) => ({ ...r, pages: viewCounts[r.company_role_id] ?? 0 })));

      const dash = t('common.none_dash', '—');
      setUsers(((usersRes.data ?? []) as Omit<PortalUserRow, 'role_label'>[]).map((u) => ({
        ...u,
        role_label: u.company_role_id ? roleLabelById.get(u.company_role_id) ?? dash : dash,
      })));

      type RawLog = Omit<AccessLogRow, 'username'> & { pfw_portal_user?: { username: string } | null };
      setLog(((logRes.data ?? []) as RawLog[]).map((l) => ({
        access_log_id: l.access_log_id,
        view_code: l.view_code,
        dataset_code: l.dataset_code,
        action: l.action,
        created_at: l.created_at,
        username: l.pfw_portal_user?.username ?? dash,
      })));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run when the selected company changes and whenever the active locale changes
  // (role display names follow it — §8). `t` is intentionally NOT a dependency.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [companyId, activeLocale]);

  const segments: { key: Segment; label: string }[] = [
    { key: 'roles', label: t('mobile.pfw.seg.roles', 'Roles') },
    { key: 'users', label: t('mobile.pfw.seg.users', 'Users') },
    { key: 'activity', label: t('mobile.pfw.seg.activity', 'Activity') },
  ];

  const activeFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.pfw.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const filteredRoles = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return roles.filter((r) => {
      if (activeFacet === 'active' && r.is_active !== 'Y') return false;
      if (activeFacet === 'inactive' && r.is_active !== 'N') return false;
      if (!q) return true;
      return r.display_text.toLowerCase().includes(q) || r.internal_code.toLowerCase().includes(q);
    });
  }, [roles, filter, activeFacet]);

  const filteredUsers = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      u.username.toLowerCase().includes(q) ||
      (u.display_name ?? '').toLowerCase().includes(q) ||
      u.role_label.toLowerCase().includes(q));
  }, [users, filter]);

  const filteredLog = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return log;
    return log.filter((l) =>
      l.username.toLowerCase().includes(q) ||
      l.action.toLowerCase().includes(q) ||
      (l.view_code ?? l.dataset_code ?? '').toLowerCase().includes(q));
  }, [log, filter]);

  const searchPlaceholder =
    segment === 'roles' ? t('mobile.pfw.search_roles', 'Search roles')
      : segment === 'users' ? t('mobile.pfw.search_users', 'Search users')
        : t('mobile.pfw.search_activity', 'Search activity');

  const pagesLabel = (n: number) => t('mobile.pfw.pages_count', { count: n, defaultValue: `${n} pages` });
  const userStatusPill = (status: string): { label: string; tone: MobilePillTone } => {
    const meta = USER_STATUS_META[status];
    return {
      label: meta ? t(meta.labelKey, meta.label) : status,
      tone: meta?.tone ?? 'info',
    };
  };
  const emptyForSegment =
    segment === 'roles' ? t('mobile.pfw.no_roles', 'No roles match')
      : segment === 'users' ? t('mobile.pfw.no_users', 'No users match')
        : t('mobile.pfw.no_activity', 'No portal activity recorded');

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.pfw.admin_title', 'Portal Administration')}
        description={t('mobile.pfw.admin_description', 'A company portal’s roles, users and access activity — read-only on mobile.')}
      />

      {/* Company (third party) picker — resolves to name (code), never the raw id. */}
      <div className="m-fieldcard">
        <div className="m-formrow">
          <label htmlFor="m-pfw-company">{t('mobile.pfw.company', 'Company (third party)')}</label>
          <select
            id="m-pfw-company"
            className="m-sel"
            value={companyId}
            onChange={(e) => { setCompanyId(e.target.value); setSelectedRole(null); setSelectedUser(null); }}
          >
            <option value="">{t('mobile.pfw.pick_company', 'Select a company…')}</option>
            {companies.map((c) => (
              <option key={c.pfw_company_id} value={String(c.pfw_company_id)}>
                {c.name} ({c.internal_code})
              </option>
            ))}
          </select>
        </div>
      </div>

      {!companyId ? (
        <MobileContentCard>
          <div className="m-empty"><Building2 /> {t('mobile.pfw.pick_company_hint', 'Select a company to view its roles, users and activity.')}</div>
        </MobileContentCard>
      ) : (
        <>
          <MobileFilterChips
            chips={segments.map((s) => ({ value: s.key, label: s.label }))}
            isActive={(v) => segment === v}
            onSelect={(v) => setSegment(v as Segment)}
          />

          <div className="m-searchbox">
            <Search />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
            />
          </div>

          {/* Independent is_active facet — only the roles list carries is_active. */}
          {segment === 'roles' && (
            <MobileFilterChips
              chips={activeFacets.map((f) => ({ value: f.key, label: f.label }))}
              isActive={(v) => activeFacet === v}
              onSelect={(v) => setActiveFacet(v as StatusFacet)}
            />
          )}

          {error && (
            <MobileContentCard>
              <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
            </MobileContentCard>
          )}

          {/* Roles */}
          {segment === 'roles' && (
            filteredRoles.length === 0 && !busy ? (
              <MobileContentCard><div className="m-empty"><Inbox /> {emptyForSegment}</div></MobileContentCard>
            ) : (
              <MobileContentCard list>
                {filteredRoles.map((r) => (
                  <MobileCardRow
                    key={r.company_role_id}
                    lead={<ShieldCheck />}
                    leadTone={r.is_admin_role === 'Y' ? 'blue' : 'slate'}
                    title={r.display_text}
                    subtitle={`${r.internal_code} · ${pagesLabel(r.pages)}`}
                    trailing={
                      <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                        {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                      </MobileStatusPill>
                    }
                    chevron
                    onClick={() => setSelectedRole(r)}
                  />
                ))}
              </MobileContentCard>
            )
          )}

          {/* Users */}
          {segment === 'users' && (
            filteredUsers.length === 0 && !busy ? (
              <MobileContentCard><div className="m-empty"><Inbox /> {emptyForSegment}</div></MobileContentCard>
            ) : (
              <MobileContentCard list>
                {filteredUsers.map((u) => {
                  const s = userStatusPill(u.status);
                  return (
                    <MobileCardRow
                      key={u.portal_user_id}
                      lead={initialsOf(u.display_name || u.username)}
                      leadTone="blue"
                      title={u.display_name || u.username}
                      subtitle={`${u.username} · ${u.role_label}`}
                      trailing={<MobileStatusPill tone={s.tone}>{s.label}</MobileStatusPill>}
                      chevron
                      onClick={() => setSelectedUser(u)}
                    />
                  );
                })}
              </MobileContentCard>
            )
          )}

          {/* Activity — portal access log */}
          {segment === 'activity' && (
            filteredLog.length === 0 && !busy ? (
              <MobileContentCard><div className="m-empty"><Inbox /> {emptyForSegment}</div></MobileContentCard>
            ) : (
              <MobileContentCard list>
                {filteredLog.map((l) => (
                  <MobileCardRow
                    key={l.access_log_id}
                    lead={<ScrollText />}
                    leadTone="slate"
                    title={`${l.username} · ${l.action}`}
                    /* fk-display-ok: audit-log target spans two code spaces (pages, datasets) with no single shared name to resolve to */
                    subtitle={`${formatDateTime(l.created_at)} · ${l.view_code ?? l.dataset_code ?? t('common.none_dash', '—')}`}
                  />
                ))}
              </MobileContentCard>
            )
          )}
        </>
      )}

      {/* Role detail bottom-sheet */}
      <div className={'m-scrim' + (selectedRole ? ' m-open' : '')} onClick={() => setSelectedRole(null)} />
      <div className={'m-sheet' + (selectedRole ? ' m-open' : '')} aria-label={t('mobile.pfw.role_detail', 'Role detail')}>
        <div className="m-grabber" />
        {selectedRole && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><ShieldCheck size={20} /></span>
              <span>
                <div className="m-nm">{selectedRole.display_text}</div>
                <div className="m-em">{selectedRole.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.pfw.status', 'Status')}
                value={
                  <MobileStatusPill tone={selectedRole.is_active === 'Y' ? 'ok' : 'no'}>
                    {selectedRole.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.pfw.company_admin', 'Company admin')}
                value={
                  <MobileStatusPill tone={selectedRole.is_admin_role === 'Y' ? 'ok' : 'no'}>
                    {selectedRole.is_admin_role === 'Y' ? t('common.boolean.yes', 'Yes') : t('common.boolean.no', 'No')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.pfw.pages_granted', 'Pages (view)')} value={pagesLabel(selectedRole.pages)} />
            </MobileContentCard>
          </>
        )}
      </div>

      {/* User detail bottom-sheet */}
      <div className={'m-scrim' + (selectedUser ? ' m-open' : '')} onClick={() => setSelectedUser(null)} />
      <div className={'m-sheet' + (selectedUser ? ' m-open' : '')} aria-label={t('mobile.pfw.user_detail', 'User detail')}>
        <div className="m-grabber" />
        {selectedUser && (() => {
          const s = userStatusPill(selectedUser.status);
          return (
            <>
              <div className="m-sheet-id">
                <span className="m-av"><User size={20} /></span>
                <span>
                  <div className="m-nm">{selectedUser.display_name || selectedUser.username}</div>
                  <div className="m-em">{selectedUser.username}</div>
                </span>
              </div>
              <MobileContentCard>
                <MobileDetailRow
                  label={t('mobile.pfw.status', 'Status')}
                  value={<MobileStatusPill tone={s.tone}>{s.label}</MobileStatusPill>}
                />
                <MobileDetailRow label={t('mobile.pfw.role', 'Role')} value={selectedUser.role_label} />
              </MobileContentCard>
            </>
          );
        })()}
      </div>
    </section>
  );
}
